import React, { memo, useState, useEffect } from "react";
import MarkdownRenderer from "./chat/MarkdownRenderer";
import SourcesBanner from "./SourcesBanner";
import { pdfViewer } from "../hooks/usePdfViewer";
import { diagramEditor } from "../hooks/useDiagramEditor";
import { spreadsheetEditor } from "../hooks/useSpreadsheetEditor";
import { circuitEditor } from "../hooks/useCircuitEditor";
import WidgetRenderer from "./WidgetRenderer";
import ToolSteps from "./ToolSteps";

// ── Sub-components ───────────────────────────────────────────────────────────

const ImageLightbox = ({ src, alt, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = 'unset'; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const { createPortal } = require('react-dom');
  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.9)' }}
      onClick={onClose}
    >
      <div className="relative max-w-full max-h-full">
        <img src={src} alt={alt} className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        <button onClick={onClose} className="absolute top-2 right-2 text-white rounded-full w-8 h-8 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>✕</button>
      </div>
    </div>,
    document.body
  );
};

const DiagramCard = ({ title, xml, diagramType }) => (
  <span onClick={() => diagramEditor.open(xml, title)} className="diagram-card" style={{ cursor: 'pointer' }}>
    <span className="text-xs font-bold">DIAGRAM</span>
    <span className="text-sm truncate">{title}</span>
    <span className="text-xs">{diagramType}</span>
  </span>
);

const SpreadsheetCard = ({ title, data }) => (
  <span onClick={() => spreadsheetEditor.open(data, title)} className="diagram-card" style={{ cursor: 'pointer' }}>
    <span className="text-xs font-bold">SHEET</span>
    <span className="text-sm truncate">{title}</span>
    <span className="text-xs">{data?.length || 0} rows</span>
  </span>
);

const YouTubeEmbed = ({ video }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  return (
    <div className="my-2 rounded-lg overflow-hidden" style={{ maxWidth: '400px' }}>
      {isPlaying ? (
        <iframe
          width="100%" height="225" frameBorder="0" allowFullScreen
          src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1`}
          title={video.title}
        />
      ) : (
        <div className="relative cursor-pointer" onClick={() => setIsPlaying(true)}>
          <img src={video.thumbnail} alt={video.title} className="w-full rounded-lg" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
              <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-2" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.8))' }}>
            <div className="text-white text-xs font-medium truncate">{video.title}</div>
            <div className="text-gray-300 text-xs">{video.channelTitle}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const MediaGallery = ({ images, videos, onImageClick }) => {
  const hasImages = images && images.length > 0;
  const hasVideos = videos && videos.length > 0;
  if (!hasImages && !hasVideos) return null;

  return (
    <div className="mt-3">
      {hasVideos && (
        <div className="flex flex-wrap gap-2 mb-2">
          {videos.map((v, i) => <YouTubeEmbed key={i} video={v} />)}
        </div>
      )}
      {hasImages && (
        <div className={`flex gap-2 flex-wrap ${images.length === 1 ? '' : 'max-w-md'}`}>
          {images.slice(0, 4).map((img, i) => (
            <img
              key={i}
              src={img.url}
              alt={img.title || ''}
              className={`${images.length === 1 ? 'max-w-full' : 'w-24 h-24 object-cover'} rounded-lg hover:opacity-80 transition-opacity cursor-pointer`}
              onClick={() => onImageClick && onImageClick(img.url)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Message component ───────────────────────────────────────────────────

const Message = memo(({ text, images = [], role, fontSizeClass = "text-base", isSearching = false, media = null, sources = null, diagrams = null, spreadsheets = null, widgets = null, circuits = null, toolsUsed = null }) => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (isSearching) {
      const interval = setInterval(() => {
        setDots(prev => prev === "..." ? "" : prev + ".");
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDots("");
    }
  }, [isSearching]);

  if (typeof text !== "string") {
    console.error("Invalid message type:", text);
    return null;
  }

  const displayText = isSearching ? `${text}${dots}` : text;
  const isAI = role === "assistant" || role === "ai";

  return (
    <div className="flex flex-col items-start relative group w-full">
      {/* Tool execution rows — shown at top so users see "Searching the web…"
          before any text deltas land. Component handles its own "show only
          when there's something to show" logic. */}
      {isAI && toolsUsed && <ToolSteps tools={toolsUsed} />}
      {/* User-uploaded images */}
      {images.length > 0 && (
        <div className="mb-3">
          <div className={`flex gap-2 flex-wrap ${images.length === 1 ? '' : 'max-w-md'}`}>
            {images.map((img, idx) => (
              <img
                key={idx} src={img}
                alt={role === "user" ? `Uploaded ${idx + 1}` : "Uploaded image"}
                className={`${images.length === 1 ? 'max-w-full' : 'w-24 h-24 object-cover'} rounded-lg cursor-pointer hover:opacity-90 transition-opacity`}
                onClick={() => setLightboxImage(img)}
              />
            ))}
          </div>
          {text && !text.startsWith("Image: ") && (
            <MarkdownRenderer text={displayText} className={fontSizeClass} />
          )}
        </div>
      )}

      {/* Text content */}
      {images.length === 0 && text && (
        <MarkdownRenderer text={displayText} className={fontSizeClass} />
      )}

      {/* Lightbox */}
      <ImageLightbox
        src={lightboxImage}
        alt={role === "user" ? "Uploaded image" : "Image"}
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />

      {/* Rich media */}
      {media && isAI && <MediaGallery images={media.images} videos={media.videos} onImageClick={setLightboxImage} />}

      {/* Diagrams */}
      {diagrams && diagrams.length > 0 && isAI && (
        <div className="mt-2 flex flex-wrap gap-2">
          {diagrams.map((d, i) => <DiagramCard key={i} title={d.title} xml={d.xml} diagramType={d.type} />)}
        </div>
      )}

      {/* Spreadsheets */}
      {spreadsheets && spreadsheets.length > 0 && isAI && (
        <div className="mt-2 flex flex-wrap gap-2">
          {spreadsheets.map((s, i) => <SpreadsheetCard key={i} title={s.title} data={s.data} />)}
        </div>
      )}

      {/* Widgets */}
      {widgets && widgets.length > 0 && isAI && (
        <div className="mt-2">
          {widgets.map((w, i) => <WidgetRenderer key={i} title={w.title} html={w.html} />)}
        </div>
      )}

      {/* Circuits */}
      {circuits && circuits.length > 0 && isAI && (
        <div className="mt-2 flex flex-wrap gap-2">
          {circuits.map((c, i) => (
            <span key={i} onClick={() => circuitEditor.open(c.files, c.title)} className="diagram-card" style={{ cursor: 'pointer' }}>
              <span className="text-xs font-bold">CIRCUIT</span>
              <span className="text-sm truncate">{c.title}</span>
              <span className="text-xs">{c.files?.length || 0} files</span>
            </span>
          ))}
        </div>
      )}

      {/* Sources */}
      {sources && isAI && <SourcesBanner sources={sources} />}
    </div>
  );
});

export default Message;
